import { createSvgEl } from '@learnforge/demo-render';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import type { Point } from '../finance/series';

export interface ChartScales {
  xDomain: [number, number];
  yDomain: [number, number];
}

export interface ChartPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface ChartOptions {
  width: number;
  height: number;
  padding: ChartPadding;
  xTickCount?: number;
  yTickCount?: number;
  formatX?(v: number): string;
  formatY?(v: number): string;
}

export interface ChartHandle {
  readonly svg: SVGSVGElement;
  /** Series/area layers append here (painted above the axes group). */
  readonly plotGroup: SVGGElement;
  /** Store domains and (re)draw axes, gridlines, and ticks. */
  setScales(scales: ChartScales): void;
  /** Map a data point to screen coordinates using the current scales. */
  toScreen(p: Point): Vec2;
  destroy(): void;
}

export function createChart(parent: HTMLElement, opts: ChartOptions): ChartHandle {
  const { width, height, padding } = opts;
  const xTickCount = opts.xTickCount ?? 5;
  const yTickCount = opts.yTickCount ?? 5;

  const svg = createSvgEl('svg', {
    viewBox: `0 0 ${width} ${height}`,
    class: 'fin-stage',
  });

  const axesGroup = createSvgEl('g', { class: 'fin-axis' });
  const plotGroup = createSvgEl('g', { class: 'fin-plot' });

  svg.appendChild(axesGroup);
  svg.appendChild(plotGroup);

  parent.appendChild(svg);

  let currentScales: ChartScales = {
    xDomain: [0, 1],
    yDomain: [0, 1],
  };

  function toScreen(p: Point): Vec2 {
    const [xMin, xMax] = currentScales.xDomain;
    const [yMin, yMax] = currentScales.yDomain;

    const plotLeft = padding.left;
    const plotRight = width - padding.right;
    const plotTop = padding.top;
    const plotBottom = height - padding.bottom;

    const x = plotLeft + ((p.t - xMin) / (xMax - xMin)) * (plotRight - plotLeft);
    const y = plotBottom - ((p.value - yMin) / (yMax - yMin)) * (plotBottom - plotTop);

    return { x, y };
  }

  function setScales(scales: ChartScales): void {
    currentScales = scales;

    // Clear axes group
    while (axesGroup.firstChild) {
      axesGroup.removeChild(axesGroup.firstChild);
    }

    const [xMin, xMax] = scales.xDomain;
    const [yMin, yMax] = scales.yDomain;

    const plotLeft = padding.left;
    const plotRight = width - padding.right;
    const plotTop = padding.top;
    const plotBottom = height - padding.bottom;

    // X axis line
    const xAxisLine = createSvgEl('line', {
      x1: plotLeft,
      y1: plotBottom,
      x2: plotRight,
      y2: plotBottom,
      class: 'fin-axis-line',
    });
    axesGroup.appendChild(xAxisLine);

    // Y axis line
    const yAxisLine = createSvgEl('line', {
      x1: plotLeft,
      y1: plotTop,
      x2: plotLeft,
      y2: plotBottom,
      class: 'fin-axis-line',
    });
    axesGroup.appendChild(yAxisLine);

    // X gridlines and tick labels
    for (let i = 0; i < xTickCount; i++) {
      const t = xMin + (i / (xTickCount - 1)) * (xMax - xMin);
      const screenX = toScreen({ t, value: 0 }).x;

      const gridline = createSvgEl('line', {
        x1: screenX,
        y1: plotTop,
        x2: screenX,
        y2: plotBottom,
        class: 'fin-gridline',
      });
      axesGroup.appendChild(gridline);

      const label = opts.formatX ? opts.formatX(t) : String(t);
      const tick = createSvgEl('text', {
        x: screenX,
        y: plotBottom + 16,
        class: 'fin-tick',
        'data-axis': 'x',
        'text-anchor': 'middle',
      });
      tick.textContent = label;
      axesGroup.appendChild(tick);
    }

    // Y gridlines and tick labels
    for (let i = 0; i < yTickCount; i++) {
      const value = yMin + (i / (yTickCount - 1)) * (yMax - yMin);
      const screenY = toScreen({ t: 0, value }).y;

      const gridline = createSvgEl('line', {
        x1: plotLeft,
        y1: screenY,
        x2: plotRight,
        y2: screenY,
        class: 'fin-gridline',
      });
      axesGroup.appendChild(gridline);

      const label = opts.formatY ? opts.formatY(value) : String(value);
      const tick = createSvgEl('text', {
        x: plotLeft - 8,
        y: screenY,
        class: 'fin-tick',
        'data-axis': 'y',
        'text-anchor': 'end',
        'dominant-baseline': 'middle',
      });
      tick.textContent = label;
      axesGroup.appendChild(tick);
    }
  }

  function destroy(): void {
    if (svg.parentNode) {
      svg.parentNode.removeChild(svg);
    }
  }

  return {
    svg,
    plotGroup,
    setScales,
    toScreen,
    destroy,
  };
}
